import { Controller } from "@hotwired/stimulus";

// LRPO-5507 (item 1) — the URL key that remembers WHICH preset produced the
// applied filters. It is deliberately NOT in LeadFilters::PERMITTED_PARAM_SHAPE:
// the server must ignore it entirely, so it filters nothing, renders no chip, and
// never inflates the filter count. It exists purely so the drawer can re-select
// the picker after a full page load.
const PRESET_ID_KEY = "preset_id";

export default class extends Controller {
  static targets = ["picker", "saveBtn", "nameInput", "saveActions", "notice", "manageLink"];
  static values = { endpoint: String, eventExhibitorId: Number, maxPresets: { type: Number, default: 50 } };
  static outlets = ["filter-diff"];

  connect() {
    this._csrf = document.querySelector('meta[name="csrf-token"]')?.content;
    this._presetCache = [];
    // The picker is a real form control: naming it puts preset_id into the draft,
    // so filter_drawer#_buildUrl writes it to the URL on Apply and it survives a
    // reload. Set here rather than in the partial so the key and the controller
    // that owns it stay in one place.
    if (this.hasPickerTarget) this.pickerTarget.name = PRESET_ID_KEY;
    // Armed for the first _renderPicker() only — see _selectPresetFromUrl.
    this._initialLoad = true;
    this._loadList();

    // Once the user edits ANY other control the draft is no longer the preset
    // they loaded, so the picker must stop claiming it is. Suppressed during a
    // preset load, whose applyValues() cascade fires input/change on every
    // control it touches.
    this._onUserEdit = (event) => {
      if (this._loadingPreset) return;
      if (!this.hasPickerTarget || !this.pickerTarget.value) return;
      if (event.target === this.pickerTarget) return;
      this.pickerTarget.value = "";
    };
    this.element.addEventListener("input", this._onUserEdit);
    this.element.addEventListener("change", this._onUserEdit);

    // Coalesce mutation bursts and input/change events into a single
    // per-frame visibility update. _updateSaveBtnVisibility calls
    // filterDiffOutlet.serializeDraft() which walks every named input in
    // the drawer — running it on every keystroke into a Select2 search
    // (which fires input + bubbles change for ~250 option toggles via
    // MutationObserver) costs tens of seconds per character.
    this._pendingFrame = null;
    this._onDiff = () => {
      if (this._pendingFrame != null) return;
      this._pendingFrame = requestAnimationFrame(() => {
        this._pendingFrame = null;
        this._updateSaveBtnVisibility();
      });
    };
    this.element.addEventListener("input", this._onDiff);
    this.element.addEventListener("change", this._onDiff);
    this.element.addEventListener("filter-diff:values-applied", this._onDiff);
    setTimeout(() => this._updateSaveBtnVisibility(), 0);

    this._onSaveKey = (event) => {
      if (event.key === "Enter") { event.preventDefault(); this.save(); }
      else if (event.key === "Escape") { this._cancelSave(); }
    };
  }

  disconnect() {
    this.element.removeEventListener("input", this._onDiff);
    this.element.removeEventListener("change", this._onDiff);
    this.element.removeEventListener("filter-diff:values-applied", this._onDiff);
    this.element.removeEventListener("input", this._onUserEdit);
    this.element.removeEventListener("change", this._onUserEdit);
    if (this._pendingFrame != null) {
      cancelAnimationFrame(this._pendingFrame);
      this._pendingFrame = null;
    }
    if (this._saveKeyAttached && this.hasNameInputTarget) {
      this.nameInputTarget.removeEventListener("keydown", this._onSaveKey);
      this._saveKeyAttached = false;
    }
  }

  // ─── List + load ──────────────────────────────────────────────────────────
  async _loadList() {
    try {
      const res = await fetch(this.endpointValue, {
        credentials: "same-origin", headers: { Accept: "application/json" }
      });
      if (!res.ok) return;
      this._presetCache = await res.json();
      this._renderPicker();
    } catch { /* soft fail */ }
  }

  _renderPicker() {
    const picker = this.pickerTarget;
    // Clear via safe DOM removal (no innerHTML) per project XSS hygiene.
    while (picker.firstChild) picker.removeChild(picker.firstChild);
    const ph = document.createElement("option");
    ph.value = "";
    ph.textContent = this._presetCache.length ? "Select a preset…" : "No saved presets";
    picker.appendChild(ph);
    this._presetCache.forEach((p) => {
      const opt = document.createElement("option");
      opt.value = String(p.id);
      opt.textContent = p.name;
      picker.appendChild(opt);
    });
    // LRPO-5507 (item 1) — re-select the preset the applied URL came from. The
    // list arrives async, so this can only run once the options exist: the picker
    // used to be left on the placeholder after every full page load, reading
    // "Select a preset…" over that preset's own filters.
    this._selectPresetFromUrl();
  }

  _selectPresetFromUrl() {
    // Only the CONNECT-time list load restores from the URL onto a baseline that
    // filter_diff captured before us. _loadList() also re-runs after a save, and
    // rebaselining there would absorb the user's still-unapplied draft into the
    // baseline — so the rebaseline below is armed for the first pass only.
    const initialLoad = this._initialLoad;
    this._initialLoad = false;

    const id = new URLSearchParams(window.location.search).get(PRESET_ID_KEY);
    if (!id) return;
    // Only select an id we actually offer — a stale bookmark of a deleted preset
    // must not leave the picker on a phantom value.
    if (!this._presetCache.some((p) => String(p.id) === String(id))) return;
    if (this.pickerTarget.value === String(id)) return;

    this.pickerTarget.value = String(id);

    // LRPO-5507 — the picker is a REAL form control (name = preset_id), so it is
    // part of the draft filter_diff snapshots. _loadList() is async, so this write
    // lands long AFTER filter_diff#connect captured its baseline with the picker
    // still EMPTY — leaving an untouched ?preset_id= page permanently "dirty":
    // isDirty() was true on a pristine drawer, so merely opening and closing it
    // stashed a bogus draft and the next open raised the spurious "restore unsaved
    // edits?" banner. Re-snapshot so the RESTORED selection IS the baseline.
    if (initialLoad && this.hasFilterDiffOutlet) this.filterDiffOutlet.rebaseline();
  }

  async load(event) {
    const id = event.target.value;
    if (!id) return;
    try {
      const res = await fetch(`${this.endpointValue}/${id}/resolve_for_event`, {
        method: "POST", credentials: "same-origin",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": this._csrf, Accept: "application/json" },
        body: JSON.stringify({ event_exhibitor_id: this.eventExhibitorIdValue })
      });
      if (!res.ok) { this._showError("Couldn't load preset."); return; }
      const { params, skipped } = await res.json();

      // Threshold rule: 0 silent, 1-2 inline, 3+ OR >50% confirmation
      const totalKeys = Object.keys(params).length + skipped.length;
      const skippedPct = totalKeys === 0 ? 0 : (skipped.length / totalKeys);

      if (skipped.length >= 3 || skippedPct > 0.5) {
        const proceed = window.confirm(
          `This preset was created for a different event. ${skipped.length} of ${totalKeys} filters don't apply here. Load the ${Object.keys(params).length} that do?`
        );
        if (!proceed) {
          event.target.value = "";
          return;
        }
      }

      // BUG #9 (load boundary): ResolveForEvent returns a NESTED cfv hash
      // (cfv: { "<id>": [...] }), but applyValues() matches FLAT bare keys
      // (cfv[<id>]) against the <select name="cfv[<id>][]"> widgets. Expand
      // back to flat keys before applying so cfv selections actually restore.
      //
      // LRPO-5507 — applyValues CLEARS every input whose key is absent from the
      // hash, and the picker is now one of those inputs (name="preset_id"), so it
      // would blank the very selection the user just made. Carry preset_id in the
      // hash and suppress _onUserEdit for the duration of the cascade (which fires
      // input/change on every control it touches, and would otherwise read as the
      // user editing away from the preset).
      if (this.hasFilterDiffOutlet) {
        this._loadingPreset = true;
        try {
          const values = this._expandCfvFromResolved(params);
          values[PRESET_ID_KEY] = String(id);
          this.filterDiffOutlet.applyValues(values);
        } finally {
          this._loadingPreset = false;
        }
      }
      this._showSkippedNotice(skipped);
    } catch { this._showError("Couldn't load preset."); }
  }

  _showSkippedNotice(skipped) {
    if (!this.hasNoticeTarget) return;
    if (!skipped || skipped.length === 0) {
      this.noticeTarget.hidden = true;
      this.noticeTarget.textContent = "";
      return;
    }
    const items = skipped.map((s) => `${s.label || s.key} (${s.reason})`).join("; ");
    this.noticeTarget.textContent =
      `${skipped.length} filter${skipped.length === 1 ? "" : "s"} from this preset don't apply to this event and were skipped: ${items}.`;
    this.noticeTarget.classList.remove("filter-drawer__notice--error");
    this.noticeTarget.classList.add("filter-drawer__notice");
    this.noticeTarget.hidden = false;
  }

  // ─── Save ──────────────────────────────────────────────────────────────────
  startSave() {
    if (this._presetCache.length >= this.maxPresetsValue) {
      this._showError(`You've reached the preset limit (${this.maxPresetsValue}). Delete one before saving a new preset.`);
      return;
    }
    if (!this.hasNameInputTarget) return;
    this.nameInputTarget.hidden = false;
    this.nameInputTarget.value = "";
    if (this.hasSaveActionsTarget) this.saveActionsTarget.hidden = false;
    this._clearError();
    requestAnimationFrame(() => this.nameInputTarget.focus());
    if (!this._saveKeyAttached) {
      this.nameInputTarget.addEventListener("keydown", this._onSaveKey);
      this._saveKeyAttached = true;
    }
  }

  // LRPO-5507 (item 1) — public, so the Cancel button beside the name input can
  // reach it via data-action. It used to be private and Escape-only.
  cancelSave() {
    this._cancelSave();
  }

  _cancelSave() {
    if (this.hasNameInputTarget) this.nameInputTarget.hidden = true;
    if (this.hasSaveActionsTarget) this.saveActionsTarget.hidden = true;
    if (this._saveKeyAttached) {
      this.nameInputTarget.removeEventListener("keydown", this._onSaveKey);
      this._saveKeyAttached = false;
    }
  }

  async save() {
    if (!this.hasFilterDiffOutlet) return;
    const name = (this.nameInputTarget.value || "").trim();
    if (!name) { this._showError("Preset name cannot be empty."); return; }

    // BUG #9 (save boundary): serializeDraft() emits cfv demographics under
    // FLAT bracket-string keys (cfv[<id>]). The leads-filter URL flow relies
    // on Rails bracket-parsing those, but this POST sends a JSON body — which
    // Rails does NOT bracket-parse — and strong params permit only a NESTED
    // cfv:{} hash. Fold flat cfv[<id>] keys into nested cfv:{ "<id>": ... }
    // so the server actually stores them.
    //
    // LRPO-5507 — preset_id is drawer state (which preset is SELECTED), not a
    // filter. Saving a tweaked preset as a new one must not bury the original's
    // id inside the new preset's params.
    const draft = this._foldCfvForSave(this._withoutPresetId(this.filterDiffOutlet.serializeDraft()));

    try {
      const res = await fetch(this.endpointValue, {
        method: "POST", credentials: "same-origin",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": this._csrf, Accept: "application/json" },
        body: JSON.stringify({ lead_filter_preset: { name, params: draft, labels: {} } })
      });

      if (res.status === 422) {
        const body = await res.json();
        const msg = body.errors?.name?.[0] ? `Name: ${body.errors.name[0]}` :
                    body.errors?.base?.[0] || "Save failed.";
        this._showError(msg);
        return;
      }
      if (!res.ok) { this._showError("Save failed."); return; }

      this._cancelSave();
      this._clearError();
      await this._loadList();
      // LRPO-5507 (item 1) — a successful save used to be completely silent: the
      // name field just vanished, with nothing to say the preset had been stored.
      this._showNotice(`Preset "${name}" saved.`);
    } catch { this._showError("Network error while saving."); }
  }

  _withoutPresetId(draft) {
    const out = { ...(draft || {}) };
    delete out[PRESET_ID_KEY];
    return out;
  }

  _showNotice(text) {
    if (!this.hasNoticeTarget) return;
    this.noticeTarget.textContent = text;
    this.noticeTarget.classList.remove("filter-drawer__notice--error");
    this.noticeTarget.classList.add("filter-drawer__notice");
    this.noticeTarget.hidden = false;
  }

  _showError(text) {
    if (!this.hasNoticeTarget) return;
    this.noticeTarget.textContent = text;
    this.noticeTarget.classList.remove("filter-drawer__notice");
    this.noticeTarget.classList.add("filter-drawer__notice", "filter-drawer__notice--error");
    this.noticeTarget.hidden = false;
  }
  _clearError() {
    if (!this.hasNoticeTarget) return;
    this.noticeTarget.hidden = true;
    this.noticeTarget.textContent = "";
  }

  // ─── cfv flat ↔ nested conversions (BUG #9) ───────────────────────────────
  // Pure transforms returning a NEW outer object (input never mutated; value
  // arrays are shared by reference but not modified). The server contract is
  // nested everywhere (strong-params permit ↔ DB ↔ ResolveForEvent output);
  // the drawer's serialize/applyValues contract is flat (cfv[<id>] matches the
  // <select name="cfv[<id>][]"> widgets). These bridge the two at the preset
  // boundaries only — same-event round-trip, field-id match. (No cfv label
  // fallback / cross-event remap here; that's a separate enhancement.)

  // Flat → nested: { "cfv[3]": v, "state": [] } → { "state": [], cfv: { "3": v } }.
  // Only numeric ids are folded; non-numeric "cfv[…]" keys pass through.
  _foldCfvForSave(draft) {
    const out = {};
    let cfv = null;
    Object.keys(draft || {}).forEach((key) => {
      const m = /^cfv\[(\d+)\]$/.exec(key);
      if (m) {
        (cfv ||= {})[m[1]] = draft[key];
      } else {
        out[key] = draft[key];
      }
    });
    if (cfv) out.cfv = cfv;
    return out;
  }

  // Nested → flat: { cfv: { "3": v }, "state": [] } → { "state": [], "cfv[3]": v }.
  _expandCfvFromResolved(params) {
    const out = {};
    const src = params || {};
    Object.keys(src).forEach((key) => {
      if (key === "cfv") return;
      out[key] = src[key];
    });
    const cfv = src.cfv;
    if (cfv && typeof cfv === "object") {
      Object.keys(cfv).forEach((id) => {
        out[`cfv[${id}]`] = cfv[id];
      });
    }
    return out;
  }

  // ─── Save button conditional visibility ───────────────────────────────────
  _updateSaveBtnVisibility() {
    if (!this.hasSaveBtnTarget || !this.hasFilterDiffOutlet) return;
    const draft = this.filterDiffOutlet.serializeDraft();
    const hasFilters = Object.keys(draft).length > 0;
    this.saveBtnTarget.hidden = !hasFilters;
  }
}
